// v0.6.2: Animated background for the app shell.
//
// A field of small multicoloured dots that drift slowly up
// the page. Pure CSS animation — no canvas, no requestAnimationFrame,
// no per-frame JS work. Each dot is a single fixed-positioned div
// with a CSS keyframe that translates it from below the viewport
// to above. The component generates 36 dots at mount with
// randomised parameters (size, colour, x-offset, animation
// duration, animation delay) and lets the browser handle the
// animation indefinitely.
//
// Design intent:
//   - Stark but not flat. The dark `#0b0d12` background alone
//     reads as "early 2000s admin tool" — the dots add a
//     sense of motion and life without competing for
//     attention with the actual UI.
//   - Subtle. Opacity 0.15-0.4 per dot, so a player who's
//     reading the dashboard doesn't see the background at
//     all. The "ah, there's motion in the background" only
//     registers when the user actually looks at the page
//     chrome.
//   - Performance. 36 GPU-accelerated divs is fine on every
//     device. No layout thrash (the animation only touches
//     `transform` and `opacity`, both compositor-only).
//   - A11y. Respects `prefers-reduced-motion: reduce` —
//     under reduced motion the dots render stationary in
//     their initial positions (the animation is paused,
//     not removed, so the visual mass is consistent). The
//     component also adds `aria-hidden="true"` and a
//     `pointer-events: none` so the dots are pure
//     decoration and never block clicks on the UI below.
//   - Stable. The dots are generated once on mount and
//     don't change. No "every page load reshuffles" effect
//     that draws the eye; the field is constant across
//     navigations.

import { useEffect, useState, useMemo } from 'react';

// 6 hues picked from the accent palette + a couple of
// complementary tones. Kept low-saturation so the dots
// don't fight the brand colours when they pass behind
// highlighted UI.
const DOT_COLORS = [
  '#7c5cff', // accent (purple)
  '#3ecf8e', // success (green)
  '#f5a524', // warn (amber)
  '#60a5fa', // blue
  '#f472b6', // pink
  '#22d3ee', // cyan
];

interface DotSpec {
  id: number;
  color: string;
  // 0-100: percentage of the viewport width
  xPct: number;
  // pixel size (2-5)
  size: number;
  // 0.15-0.4
  opacity: number;
  // animation duration in seconds (20-40s per cycle)
  duration: number;
  // negative delay so the dot starts mid-cycle on first
  // render (otherwise the first few seconds show all dots
  // entering from the bottom in a "wave" that draws the
  // eye; staggered start positions are more natural).
  delay: number;
}

// Deterministic PRNG. Mulberry32 — small, fast, good enough
// for "make a random-looking field of dots that doesn't
// change on every render". The seed is fixed so two clients
// with the same seed see the same field, which matters for
// screenshot diffs in tests but not for production. The
// seed is the same constant across all renders, so a single
// component instance is stable across re-renders too.
function mulberry32(seed: number): () => number {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DOT_COUNT = 60;
const SEED = 0x5a3e91f7; // arbitrary; the value doesn't matter, only that it's fixed

function generateDots(): DotSpec[] {
  const rand = mulberry32(SEED);
  const dots: DotSpec[] = [];
  for (let i = 0; i < DOT_COUNT; i += 1) {
    dots.push({
      id: i,
      color: DOT_COLORS[Math.floor(rand() * DOT_COLORS.length)]!,
      xPct: rand() * 100,
      // 3-7px (was 2-5). At 100% zoom these are
      // small but clearly visible particles; on a
      // wall-TV at 1m+ distance they read as a
      // soft field of colour rather than a hard
      // pixel.
      size: 3 + rand() * 4,
      // 0.4-0.7 (was 0.15-0.4). Bumped because the
      // pre-M5.11 opacity range made the dots too
      // subtle to register on a standard-density
      // monitor — the user reported 'I don't see
      // any dots' even after 3 fix commits, which
      // means the field was too faint to be
      // noticed. The 0.4-0.7 range is still
      // 'background ornament' territory (the dots
      // never compete with foreground text), but
      // they actually read as a soft moving field
      // on a standard monitor.
      opacity: 0.4 + rand() * 0.3,
      duration: 20 + rand() * 20, // 20-40s
      // Negative delay so each dot starts at a random
      // point in its cycle, not all at 0. This is what
      // makes the field look "always populated" instead
      // of "all dots enter together on first render".
      delay: -(rand() * 40), // 0 to -40s
    });
  }
  return dots;
}

export function AnimatedBackground() {
  // Generate the dot field once on first render. useMemo
  // with an empty dep array keeps it stable across re-renders.
  const dots = useMemo(generateDots, []);

  // Track reduced-motion so we can pause the animation.
  // The check runs once on mount + on media-query change;
  // the value is read on each render to pick the right
  // animation-play-state.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduceMotion(mq.matches);
    setReduceMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="animated-background"
      data-dot-count={dots.length}
      // pointer-events: none is critical — without it, the
      // fixed-positioned dots would intercept clicks on
      // the UI below. position: fixed inset-0 covers the
      // whole viewport.
      //
      // z-index: 0 (the default) so the dots sit on top
      // of the body's bg-bg but BELOW the app's content.
      // The first AnimatedBackground commit had
      // -z-10 here, which put the dots behind the body's
      // own dark background, making them invisible. The
      // app's actual content is z-10+ (the AppShell, the
      // public board, the login card) so the dots still
      // sit behind the real UI even with z-0.
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
    >
      {dots.map((d) => {
        // When motion is reduced, the CSS animation is
        // paused (animation-play-state: paused) at the dot's
        // initial position (translateY(110vh), which is
        // below the viewport). The dot would be invisible.
        // To make the field still visible as a static
        // constellation, we override the initial Y in JS
        // when reduceMotion is true: place each dot at a
        // position derived from its delay (which encodes
        // a random point in the cycle), so the field
        // looks "scattered" rather than empty.
        //
        // The math: a dot with `animation-delay: -X` and
        // `animation-duration: Y` starts at progress X/Y
        // through the keyframe. Keyframe is 110vh → -10vh
        // (range 120vh), so a progress p gives Y = 110vh -
        // 120vh * p. We pre-compute this so the static
        // (paused) field looks similar to a frozen frame
        // of the animated field.
        const cycleProgress = d.delay === 0 ? 0 : (-d.delay) / d.duration;
        const progressFrac = cycleProgress - Math.floor(cycleProgress);
        const initialYVh = reduceMotion ? 110 - 120 * progressFrac : 110;
        return (
          <span
            key={d.id}
            className="mirabili-float-up absolute rounded-full"
            style={{
              left: `${d.xPct}%`,
              width: `${d.size}px`,
              height: `${d.size}px`,
              backgroundColor: d.color,
              opacity: d.opacity,
              animationDuration: `${d.duration}s`,
              animationDelay: `${d.delay}s`,
              // Under reduced motion, pause the animation
              // rather than removing the dots entirely —
              // removing them would cause a visible layout
              // shift (the field of colour disappearing) for
              // users who toggle the preference mid-session.
              animationPlayState: reduceMotion ? 'paused' : 'running',
              // Initial Y position. When animating, the
              // keyframe drives this. When paused, JS
              // computes a representative position from the
              // delay (see comment above) so the dot is on
              // screen rather than 110vh below it.
              transform: `translateY(${initialYVh}vh)`,
              // boxShadow gives each dot a faint glow that
              // makes it read as a soft particle rather
              // than a hard pixel. Sized to match the dot
              // radius. Under reduced motion this is
              // unchanged — it's a static visual treatment.
              boxShadow: `0 0 ${d.size * 2}px ${d.color}`,
              willChange: 'transform, opacity',
            }}
          />
        );
      })}
    </div>
  );
}
